import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./MobileMenu.scss";
import NetsLogo from "../../../../UI/NetsLogo/NetsLogo";
import { menuRef, sectionForPath } from "../../../../Modules/MenuModel/MenuLogic";
import ThemeToggle from "../../../../theme/ThemeToggle";

/*
  Мобильное меню (≤768px): карточка-поповер из-под шапки, под кнопкой-крестиком.
  Верхний уровень — плоский список menuRef, без «Ещё» и без аккордеона.
  Под разделом текущей страницы всегда видны её якоря из menuLink.
  Данные не дублируются: menuRef + sectionForPath из MenuLogic, menuLink — проп.

  Классы с префиксом mmenu_ — свой неймспейс, без общих имён вроде .menu__box.
*/

// Фикс-шапка 70px + воздух: на столько поднимаем цель якоря при скролле.
const HEADER_OFFSET = 86;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const MobileMenu = ({ menuLink }) => {
  const [open, setOpen] = useState(false);
  const [currentAnchor, setCurrentAnchor] = useState(null);
  const { pathname } = useLocation();

  const buttonRef = useRef(null);
  const cardRef = useRef(null);
  const wasOpen = useRef(false);

  const activeSection = sectionForPath(pathname);
  // menuLink может прийти пустым (большинство страниц) — тогда подпунктов нет
  const subLinks = useMemo(() => menuLink ?? [], [menuLink]);

  const close = useCallback(() => setOpen(false), []);

  // Смена роута закрывает меню
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Блокировка скролла страницы, пока меню открыто
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc, ловушка фокуса и перевод фокуса внутрь карточки
  useEffect(() => {
    if (!open) return undefined;
    const card = cardRef.current;
    if (!card) return undefined;

    const focusable = () =>
      [...card.querySelectorAll('a[href], button:not([disabled])')];

    focusable()[0]?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Закрылись — возвращаем фокус на кнопку (но не при первом рендере)
  useEffect(() => {
    if (wasOpen.current && !open) buttonRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  // Какой якорь сейчас на экране. Считаем в момент открытия: пока меню
  // открыто, скролл страницы заблокирован, поэтому значение измениться
  // не может — наблюдатель за пересечениями был бы лишней деталью.
  const pickCurrentAnchor = useCallback(() => {
    const hashes = subLinks
      .map((item) => String(item.link))
      .filter((link) => link.startsWith("#"));
    if (!hashes.length) return;

    let current = null;
    hashes.forEach((hash) => {
      const target = document.getElementById(hash.slice(1));
      if (!target) return;
      const top = target.getBoundingClientRect().top - HEADER_OFFSET;
      // блок уже начался выше линии шапки — значит смотрим на него
      if (top <= 8) current = hash;
      // либо он первый, кто вот-вот появится
      else if (!current && top < window.innerHeight * 0.45) current = hash;
    });
    setCurrentAnchor(current);
  }, [subLinks]);

  useEffect(() => {
    if (open) pickCurrentAnchor();
  }, [open, pickCurrentAnchor]);

  // Тап по якорю: закрыть меню и плавно доскроллить с учётом фикс-шапки
  const goToAnchor = (event, link) => {
    const target = document.getElementById(String(link).slice(1));
    if (!target) return; // якоря нет на странице — оставляем штатное поведение
    event.preventDefault();
    setOpen(false);
    setCurrentAnchor(link);
    requestAnimationFrame(() => {
      const top = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
      window.scrollTo({ top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
      window.history.replaceState(null, "", link);
      // replaceState событие не шлёт, а на хэш завязаны секции (например,
      // вкладка акций в тарифах) — сообщаем о смене сами.
      window.dispatchEvent(new Event("hashchange"));
    });
  };

  const renderSubLinks = () => (
    <div className="mmenu__sub">
      {subLinks.map((sub) =>
        String(sub.link).startsWith("#") ? (
          <a
            key={sub.name}
            href={sub.link}
            className={
              "mmenu__sublink" + (currentAnchor === sub.link ? " is-current" : "")
            }
            onClick={(event) => goToAnchor(event, sub.link)}
          >
            {sub.name}
          </a>
        ) : (
          <NavLink
            key={sub.name}
            to={sub.link}
            className="mmenu__sublink"
            onClick={close}
          >
            {sub.name}
          </NavLink>
        )
      )}
    </div>
  );

  return (
    <div className="mmenu">
      <div className="mmenu__bar">
        <NavLink to="/" className="mmenu__logo" onClick={close}>
          <NetsLogo />
        </NavLink>

        <button
          ref={buttonRef}
          type="button"
          className={"mmenu__toggle" + (open ? " is-open" : "")}
          aria-expanded={open}
          aria-controls="mmenu-card"
          aria-label={open ? "Закрыть меню" : "Открыть меню"}
          onClick={() => setOpen((value) => !value)}
        >
          <span className="mmenu__toggle-icon" aria-hidden="true" />
        </button>
      </div>

      {open && (
        <>
          <div className="mmenu__backdrop" onClick={close} aria-hidden="true" />
          <div
            id="mmenu-card"
            ref={cardRef}
            className="mmenu__card"
            role="dialog"
            aria-modal="true"
            aria-label="Меню сайта"
          >
            <nav className="mmenu__list">
              {menuRef.map((item) => {
                const isActive = !item.external && item.path === activeSection;
                return (
                  <div className="mmenu__group" key={item.name}>
                    {item.external ? (
                      <a href={item.path} className="mmenu__row" onClick={close}>
                        <span className="mmenu__label">{item.name}</span>
                        {item.isNew && <span className="mmenu__badge">Новое</span>}
                        <svg
                          className="mmenu__ext"
                          viewBox="0 0 16 16"
                          aria-hidden="true"
                        >
                          <path
                            d="M6 3h7v7M13 3L4 12"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </a>
                    ) : (
                      <NavLink
                        to={item.path}
                        className={"mmenu__row" + (isActive ? " is-active" : "")}
                        aria-current={isActive ? "page" : undefined}
                        onClick={close}
                      >
                        <span className="mmenu__label">{item.name}</span>
                      </NavLink>
                    )}

                    {isActive && subLinks.length > 0 && renderSubLinks()}
                  </div>
                );
              })}
            </nav>

            <div className="mmenu__footer">
              <ThemeToggle />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileMenu;
